import Vue from 'vue'
import vuetify from '../vuetify'
import Toast from './toast'

const _DEFAULTS = {
    timeout: 6000,
    type: 'primary',
    offsetY: 8
}

let queue = []
let visibleEls = []
let visibleProps = {}
let toastId = 1
let showing = false

export { Toast }
export default {
    defaults(defaults) {
        if (!defaults) return _DEFAULTS
        Object.assign(_DEFAULTS, defaults)
    },
    /**
     * Open new toast.
     * @param {Object|string} params
     * @param {string?} params.title
     * @param {string?} params.text
     * @param {string?} params.type=primary
     * @param {string?} params.action
     * @param {function?} params.actionHandler
     * @param {Object?} params.options
     * @param {number?} params.timeout=6000
     * @param {boolean?} params.closeable
     * @param {boolean?} params.queue
     * @param {boolean?} params.vertical
     * @param {number?} params.offsetY
     * @param {function?} params.onMounted
     * @return {{id: string, text: string, title: *, type: (*|string)}|void}
     */
    open(params) {
        if (typeof params === 'string') params = {text: params}
        if (!params.text) return console.error('[$toast] no text supplied!')

        const propsData = {
            id: `v-toast-${toastId++}`,
            title: params.title,
            text: params.text,
            type: params.type || _DEFAULTS.type,
            offsetY: params.offsetY
        }

        const color = propsData.type
        const timeout = typeof params.timeout !== 'undefined' ? Number(params.timeout) : _DEFAULTS.timeout
        const closeable = typeof params.closeable === 'boolean' ? params.closeable : timeout <= 0
        const queued = typeof params.queue === 'boolean' ? params.queue : true

        // options for v-snackbar
        const defaultOptions = {
            color,
            app: false,
            absolute: true,
            bottom: true,
            right: true,
            closeable,
            vertical: params.vertical || false,
            timeout,
            multiLine: !!propsData.title || propsData.text.length > 80,
            action: params.action
        }

        propsData.options = Object.assign(defaultOptions, params.options)
        propsData.actionHandler = params.actionHandler || function() {}

        // no timeout = spawn directly, no queue
        if (propsData.options.timeout <= 0 || !queued) {
            spawn(propsData, (el) => {
                if (typeof params.onMounted === 'function') params.onMounted(el)
            })
        }
        else {
            queue.push(propsData)
            processQueue()
        }

        return propsData
    },
    close() {
        visibleEls.forEach(el => {
            if (typeof el.close === 'function') el.close()
        })
        visibleEls = []
        visibleProps = {}
    },
    test() {
        showing = false
        this.defaults({timeout: 10000, offsetY: 48})
        this.open('Einfacher String')
        this.open({text: 'Mit Timeout 10s', timeout: 10000, type: 'error'})
        setTimeout(() => {
            this.open({text: 'Ohne Timeout', timeout: -1, type: 'warning'})
            setTimeout(() => {
                this.open({text: 'Ohne Timeout', title: 'Mit Titel', timeout: -1, type: 'accent'})
                setTimeout(() => {
                    this.open({text: 'Nicht schließen mit Timeout', title: 'Mit Titel', closeable: false, timeout: 10000, type: 'primary'})
                    this.open({text: 'Nicht schließen ohne Timeout', closeable: false, timeout: -1, type: 'secondary'})
                }, 5000)
            }, 4000)
        }, 3000)
    }
}

function processQueue() {
    if (queue.length < 1 || showing) return

    spawn(queue[0])
    showing = true
    queue.shift()
}

function spawn(propsData, callback) {
    const {id, ...other} = propsData
    const $el = document.querySelector(`#${id}`)
    if ($el) $el.parentNode.removeChild($el)

    // find duplicate in currently visible toasts
    let dup
    for (const vp in visibleProps) {
        if (visibleProps.hasOwnProperty(vp) && visibleProps[vp].props === JSON.stringify(other)) {
            dup = vp
            break
        }
    }
    // try to update directly, if toast is already visible
    if (dup) {
        visibleProps[dup].count++
        updateTitle(dup, visibleProps[dup])
        return
    }

    visibleProps[propsData.id] = {props: JSON.stringify(other), title: other.title, count: 1}

    const ToastComponent = Vue.extend(Toast)
    new ToastComponent({
        el: document.createElement('div'),
        propsData,
        vuetify,
        onMounted(el) {
            if (!el) return
            if (!visibleEls.includes(el)) visibleEls.push(el)
            el.classList.remove('v-toast--hidden')
            refreshPositions(propsData.offsetY)
            checkDuplicates()
            if (typeof callback === 'function') callback(el)
        },
        onClose() {
            showing = false
            visibleEls = visibleEls.filter(el => el.id !== propsData.id)
            delete visibleProps[propsData.id]
            refreshPositions(propsData.offsetY)
            processQueue()
        },
        onAction() {
            propsData.actionHandler()
        }
    })
}

function refreshPositions(offsetY) {
    let visibleHeight = typeof offsetY === 'undefined' ? _DEFAULTS.offsetY : offsetY // bottom default TODO check other directions (top, left, right)
    let wrapper
    visibleEls.forEach(el => {
        wrapper = el.querySelector('.v-snack__wrapper')
        el.style.bottom = `${visibleHeight}px`
        visibleHeight += wrapper.clientHeight + 16 // margin-top 8px + margin-bottom 8px
    })
}

function checkDuplicates() {
    for (const vp in visibleProps) {
        if (visibleProps.hasOwnProperty(vp)) updateTitle(vp, visibleProps[vp])
    }
}

function updateTitle(id, {title, count} = {}) {
    const $toast = document.querySelector(`#${id}`)
    if ($toast) {
        const $title = $toast.querySelector('.title')
        if ($title) {
            const counter = count > 1 ? ` (${count})` : ''
            $title.innerText = `${title} ${counter}`.trim()
        }
    }
}
